import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronDown, X, Calendar, Download, Eye, Clock, CheckCircle, AlertCircle, Truck, Tag, Package, Shield, Edit, FileText } from 'lucide-react';
import BusinessUnitName from '../../components/BusinessUnitName';
import InboundRecordViewModal from '../../components/InboundRecordViewModal';
import businessUnitService from '../../../../../services/businessUnitService';
import { formatDate, formatTime, formatDateTime } from '../../components/utils/dateUtils';
import { useTimezone } from '../../../../../hooks/useTimezone';
import useServerTruckSearch from '../../hooks/useServerTruckSearch';
import TruckNotesThread from '../../components/TruckNotesThread';

const HistoryView = ({ truckData, openTruckEditor }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ 
    start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0], 
    end: new Date().toISOString().split('T')[0] 
  });
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [businessUnitFilter, setBusinessUnitFilter] = useState('all');
  const [loadTypeFilter, setLoadTypeFilter] = useState('all');
  const [loadMaterialFilter, setLoadMaterialFilter] = useState('all');
  const [showRecordModal, setShowRecordModal] = useState(false);

  // Get user's timezone
  const userTimezone = useTimezone();
  
  // Load business units on component mount
  useEffect(() => {
    const loadBusinessUnits = async () => {
      try {
        const units = await businessUnitService.getAllBusinessUnits();
        setBusinessUnits(units);
      } catch (error) {
        console.error('Error loading business units:', error);
      }
    };
    loadBusinessUnits();
  }, []);
  
  // Helper function for robust/fuzzy string matching
  const fuzzyMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return false;
    
    // Convert to lowercase for case-insensitive matching
    const normalizedText = text.toString().toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase();
    
    // Direct substring match
    if (normalizedText.includes(normalizedSearch)) return true;
    
    // Check if all characters of search term appear in order in the text
    let searchIndex = 0;
    for (let i = 0; i < normalizedText.length && searchIndex < normalizedSearch.length; i++) {
      if (normalizedText[i] === normalizedSearch[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === normalizedSearch.length;
  };
  
  // Filter history trucks - inbound specific
  const historyTrucks = truckData.filter(truck => {
    // A truck should be in history only if it has status='Completed' OR status='Cancelled'
    return truck.status === 'Completed' || truck.status === 'Cancelled';
  });

  // Extract unique values for filter dropdowns
  const uniqueBusinessUnits = [...new Set(historyTrucks.map(truck => truck.business_unit))].filter(Boolean);
  const uniqueLoadTypes = [...new Set(historyTrucks.map(truck => truck.load_type))].filter(Boolean);
  const uniqueLoadMaterials = [...new Set(historyTrucks.map(truck => truck.load_material))].filter(Boolean);

  const filteredTrucks = historyTrucks.filter(truck => {
    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'conforming' && !truck.conforming) return false;
      if (statusFilter === 'non-conforming' && truck.conforming) return false;
    }

    // Date range filter
    if (dateRange.start || dateRange.end) {
      // Get the UTC date from truck.created_at for filtering
      const createdDate = new Date(truck.created_at);

      // Extract the local date parts (year, month, day) to compare dates without time
      const createdYear = createdDate.getFullYear();
      const createdMonth = createdDate.getMonth() + 1; // JavaScript months are 0-based
      const createdDay = createdDate.getDate();

      // Format as YYYY-MM-DD for string comparison
      const createdDateStr = `${createdYear}-${createdMonth.toString().padStart(2, '0')}-${createdDay.toString().padStart(2, '0')}`;

      if (dateRange.start && createdDateStr < dateRange.start) return false;
      if (dateRange.end && createdDateStr > dateRange.end) return false;
    }

    // Business Unit filter
    if (businessUnitFilter !== 'all' && String(truck.business_unit) !== String(businessUnitFilter)) {
      return false;
    }

    // Load Type filter
    if (loadTypeFilter !== 'all' && truck.load_type !== loadTypeFilter) {
      return false;
    }

    // Load Material filter
    if (loadMaterialFilter !== 'all' && truck.load_material !== loadMaterialFilter) {
      return false;
    }

    // Apply search filter — match the same field set the server's search param covers.
    if (searchTerm) {
      const businessUnit = businessUnits.find(unit =>
        String(unit.id) === String(truck.business_unit)
      );
      const fields = [
        truck.carrier,
        truck.po,
        truck.conf_number,
        truck.vendor,
        truck.scheduler_notes,
        truck.receiver_notes,
        businessUnit && businessUnit.name,
      ];
      if (!fields.some((f) => f && fuzzyMatch(String(f), searchTerm))) {
        return false;
      }
    }

    return true;
  });

  // Server-side fallback to find history older than the BaseDashboard 90-day buffer.
  const { results: serverResults, status: serverSearchStatus } = useServerTruckSearch({
    mode: 'inbound',
    searchTerm,
    extraParams: {},
    enabled: !!searchTerm && searchTerm.trim().length >= 2,
  });

  const mergedTrucks = (() => {
    if (!searchTerm || serverSearchStatus !== 'ready') return filteredTrucks;
    const seen = new Set(filteredTrucks.map((t) => t.id));
    // Inbound history covers Completed (and arguably Cancelled) — match the historyTrucks gate above.
    const completedStatuses = new Set(['Completed', 'Cancelled']);
    const extras = serverResults.filter(
      (t) => completedStatuses.has(t.status) && !seen.has(t.id)
    );
    return filteredTrucks.concat(extras);
  })();

  // Sort trucks by completion date (newest first)
  const sortedTrucks = [...mergedTrucks].sort((a, b) => {
    return new Date(b.putaway_at) - new Date(a.putaway_at);
  });

  // The formatting functions are now imported from dateUtils
  
  // Get conforming badge info
  const getConformingBadge = (conforming) => {
    if (conforming) {
      return { 
        color: 'bg-green-600 text-white',
        icon: <CheckCircle className="w-4 h-4 mr-1" />,
        label: 'Conforming'
      };
    } else {
      return { 
        color: 'bg-red-600 text-white',
        icon: <AlertCircle className="w-4 h-4 mr-1" />,
        label: 'Non-Conforming'
      };
    }
  };
  
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Search and filters - fixed area */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <div className="flex-1 min-w-[280px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search carrier, vendor, PO, conf #, business unit, notes…"
                className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <div className="absolute -bottom-5 left-1 text-xs text-slate-500">
                  {serverSearchStatus === 'loading' && 'Searching all history…'}
                  {serverSearchStatus === 'ready' && (
                    <>Found {sortedTrucks.length} match{sortedTrucks.length === 1 ? '' : 'es'} (incl. older history).</>
                  )}
                  {serverSearchStatus === 'error' && 'Server search failed; showing buffered matches only.'}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <Filter size={16} />
              Filters
              <ChevronDown size={16} className={`transform transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>
        
        {/* Expanded filters */}
        {filterOpen && (
          <div className="mb-4 p-4 bg-slate-700 rounded-md">
            <div className="flex flex-wrap gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Status</label>
                <select
                  className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="conforming">Conforming</option>
                  <option value="non-conforming">Non-Conforming</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Business Unit</label>
                <select
                  className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={businessUnitFilter}
                  onChange={(e) => setBusinessUnitFilter(e.target.value)}
                >
                  <option value="all">All Business Units</option>
                  {uniqueBusinessUnits.map(unitId => {
                    const unit = businessUnits.find(u => String(u.id) === String(unitId));
                    return (
                      <option key={unitId} value={unitId}>
                        {unit ? unit.name : `Unit ${unitId}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Load Type</label>
                <select
                  className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={loadTypeFilter}
                  onChange={(e) => setLoadTypeFilter(e.target.value)}
                >
                  <option value="all">All Load Types</option>
                  {uniqueLoadTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Load Material</label>
                <select
                  className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={loadMaterialFilter}
                  onChange={(e) => setLoadMaterialFilter(e.target.value)}
                >
                  <option value="all">All Materials</option>
                  {uniqueLoadMaterials.map(material => (
                    <option key={material} value={material}>{material}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Date Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex items-end">
                <button
                  className="flex items-center gap-1 px-3 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded-md"
                  onClick={() => {
                    setStatusFilter('all');
                    setBusinessUnitFilter('all');
                    setLoadTypeFilter('all');
                    setLoadMaterialFilter('all');
                    setDateRange({
                      start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
                      end: new Date().toISOString().split('T')[0]
                    });
                    setSearchTerm('');
                  }}
                >
                  <X size={16} />
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Main content - scrollable */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden px-6 pb-6">
        {/* Truck history list - scrollable */}
        <div className={`${selectedTruck ? 'w-full lg:w-1/2 pr-0 lg:pr-3' : 'w-full'} overflow-hidden flex flex-col h-full`}>
          <h3 className="text-lg font-medium text-slate-200 mb-4">
            Inbound Delivery History ({sortedTrucks.length})
          </h3>
          
          <div className="flex-1 overflow-y-auto pr-1">
            {sortedTrucks.length === 0 ? (
              <div className="bg-slate-800 rounded-lg p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-500 mb-3" />
                <p className="text-slate-400">No historical records match your current filters</p>
                {(statusFilter !== 'all' || businessUnitFilter !== 'all' || loadTypeFilter !== 'all' || loadMaterialFilter !== 'all' || dateRange.start || dateRange.end || searchTerm) && (
                  <button
                    className="mt-3 px-3 py-1 bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                    onClick={() => {
                      setStatusFilter('all');
                      setBusinessUnitFilter('all');
                      setLoadTypeFilter('all');
                      setLoadMaterialFilter('all');
                      setDateRange({
                        start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
                        end: new Date().toISOString().split('T')[0]
                      });
                      setSearchTerm('');
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {sortedTrucks.map(truck => (
                  <div 
                    key={truck.id}
                    className={`bg-slate-800 rounded-lg p-4 hover:bg-slate-750 cursor-pointer ${
                      selectedTruck && selectedTruck.id === truck.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => {
                      console.log('Selected truck data:', truck);
                      setSelectedTruck(truck);
                    }}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-slate-200">{truck.carrier}</h4>
                        <div className="flex items-center text-sm text-slate-400 mt-1">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(truck.putaway_at)}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {openTruckEditor && (
                          <button 
                            className="text-amber-400 hover:text-amber-300 flex items-center text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTruckEditor(truck);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </button>
                        )}
                        <div className={`rounded-full px-3 py-1 text-xs flex items-center ${getConformingBadge(truck.conforming).color}`}>
                          {getConformingBadge(truck.conforming).icon}
                          <span>{getConformingBadge(truck.conforming).label}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">Business Unit</p>
                        <p className="text-slate-300"><BusinessUnitName id={truck.business_unit} /></p>
                      </div>
                      {truck.po && (
                        <div>
                          <p className="text-xs text-slate-400">PO Number</p>
                          <p className="text-slate-300">{truck.po}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-400">Scheduled/Arrived</p>
                        <p className="text-slate-300">{formatTime(truck.scheduled_at, 'h:mm a', userTimezone)} / {formatTime(truck.arrived_at, 'h:mm a', userTimezone)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Load Material</p>
                        <p className="text-slate-300">{truck.load_material || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Size</p>
                        <p className="text-slate-300">
                          {truck.size} {truck.size === 1 ? 'pallet' : 'pallets'}
                        </p>
                      </div>
                    </div>
                    
                    {!truck.conforming && (
                      <div className="mt-3 text-xs bg-red-900/30 border border-red-800 rounded p-2 text-red-200">
                        Non-conforming — open record for receiver notes.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Detail view (appears when a truck is selected) */}
        {selectedTruck && (
          <div className="w-full lg:w-1/2 h-full mt-4 lg:mt-0 flex flex-col overflow-hidden">
            <div className="bg-slate-700 rounded-lg p-4 h-full flex flex-col overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-slate-200 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Delivery Record
                </h3>
                <button
                  className="text-slate-400 hover:text-slate-300"
                  onClick={() => setSelectedTruck(null)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto pr-1">
                {/* Header info */}
                <div className="bg-slate-800 rounded-md p-4 mb-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-lg font-medium text-slate-200">{selectedTruck.carrier}</h4>
                      <p className="text-sm text-slate-400">
                        <BusinessUnitName id={selectedTruck.business_unit} />
                        {selectedTruck.po && ` • PO: ${selectedTruck.po}`}
                      </p>
                    </div>
                    <div className={`rounded-full px-3 py-1 text-xs flex items-center ${getConformingBadge(selectedTruck.conforming).color}`}>
                      {getConformingBadge(selectedTruck.conforming).icon}
                      <span>{getConformingBadge(selectedTruck.conforming).label}</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-slate-400">Scheduled Date</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    {selectedTruck.dock_door && (
                      <div>
                        <p className="text-xs text-slate-400">Dock</p>
                        <p className="text-sm text-slate-300">{selectedTruck.dock_door}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-400">Arrival Date/Time</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.arrived_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Completed Date/Time</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.completed_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Putaway Date/Time</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.putaway_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Load Type</p>
                      <p className="text-sm text-slate-300">{selectedTruck.load_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Load Material</p>
                      <p className="text-sm text-slate-300">{selectedTruck.load_material || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Size</p>
                      <p className="text-sm text-slate-300">{selectedTruck.size} {selectedTruck.size === 1 ? 'pallet' : 'pallets'}</p>
                    </div>
                    {selectedTruck.secure && (
                      <div className="col-span-2">
                        <p className="text-xs flex items-center text-amber-400">
                          <Shield className="w-4 h-4 mr-1" />
                          This delivery required secure handling procedures
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Notes (read-only thread) */}
                <div className="bg-slate-800 rounded-md p-4 mb-4">
                  <h4 className="font-medium text-slate-200 mb-2">Notes</h4>
                  <TruckNotesThread
                    truckType="inbound"
                    truckId={selectedTruck.id}
                    mode="read"
                    theme="dark"
                    emptyMessage="No notes on this truck."
                  />
                </div>
                
                {/* Timeline */}
                <div className="bg-slate-800 rounded-md p-4">
                  <h4 className="font-medium text-slate-200 mb-3">Delivery Timeline</h4>
                  
                  <div className="relative pl-6 pb-2 border-l border-slate-600">
                    <div className="mb-4 relative">
                      <div className="absolute -left-[25px] bg-blue-600 rounded-full p-1">
                        <Calendar className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-sm font-medium text-slate-200">Scheduled</p>
                      <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    
                    {selectedTruck.arrived_at && (
                      <div className="mb-4 relative">
                        <div className="absolute -left-[25px] bg-green-600 rounded-full p-1">
                          <Truck className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-slate-200">Arrived</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.arrived_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}
                    
                    {selectedTruck.received_at && (
                      <div className="mb-4 relative">
                        <div className="absolute -left-[25px] bg-purple-600 rounded-full p-1">
                          <Package className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-slate-200">Received</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.received_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}
                    
                    {selectedTruck.putaway_at && (
                      <div className="mb-4 relative">
                        <div className="absolute -left-[25px] bg-teal-600 rounded-full p-1">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-slate-200">Putaway</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.putaway_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}

                    {selectedTruck.completed_at && (
                      <div className="relative">
                        <div className="absolute -left-[25px] bg-green-700 rounded-full p-1">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-slate-200">Completed</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.completed_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}

                    {/* Show cancellation in timeline if cancelled */}
                    {(selectedTruck.cancelled_at || selectedTruck.status === 'Cancelled') && (
                      <div className="relative">
                        <div className="absolute -left-[25px] bg-red-700 rounded-full p-1">
                          <X className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-red-200">Cancelled</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.cancelled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Actions - fixed at bottom */}
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-600">
                <button
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={() => setShowRecordModal(true)}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View/Export Record
                </button>
                {openTruckEditor && (
                  <button
                    className="flex items-center px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
                    onClick={() => openTruckEditor(selectedTruck)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Record
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Record View/Export Modal */}
      {showRecordModal && selectedTruck && (
        <InboundRecordViewModal
          isOpen={showRecordModal}
          onClose={() => setShowRecordModal(false)}
          record={selectedTruck}
        />
      )}
    </div>
  );
};

export default HistoryView;